// Sign-in helpers with no browser or network in them, so the logic tests can check them.

/** Email as typed, tidied: spaces trimmed, lower case. */
export function tidyEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/** The code from the sign-in email: digits only (spaces and dashes dropped). */
export function tidyCode(raw: string): string {
  return raw.replace(/[\s-]/g, '');
}

/** Supabase sends 6 digits by default; the length can be set from 6 to 10. */
export function looksLikeCode(code: string): boolean {
  return /^\d{6,10}$/.test(code);
}

interface AuthErrorLike {
  code?: string;
  status?: number;
  message?: string;
}

/** Plain words for what went wrong when asking for a link or checking a code. */
export function sendProblem(error: AuthErrorLike): string {
  const msg = (error.message ?? '').toLowerCase();
  if (error.status === 429 || error.code === 'over_email_send_rate_limit' || msg.includes('rate limit')) {
    return 'Too many sign-in emails in a short time. Wait a minute, then try again.';
  }
  if (error.code === 'otp_disabled' || msg.includes('signups not allowed')) {
    return 'That address isn’t invited. Check the spelling, or ask for an invitation.';
  }
  if (error.code === 'email_address_invalid' || (msg.includes('invalid') && msg.includes('email'))) {
    return 'That doesn’t look like an email address.';
  }
  return 'The sign-in email couldn’t be sent. Check your connection and try again.';
}

export function codeProblem(error: AuthErrorLike): string {
  const msg = (error.message ?? '').toLowerCase();
  if (error.status === 429 || msg.includes('rate limit')) {
    return 'Too many tries. Wait a minute, then try again.';
  }
  if (error.code === 'otp_expired' || msg.includes('expired') || msg.includes('invalid')) {
    return 'That code didn’t work or has expired. Check it, or send a new email.';
  }
  return 'That code couldn’t be checked. Check your connection and try again.';
}

/** Seconds to wait before "Send again" works (Supabase allows one email a minute per address). */
export const RESEND_WAIT_SECONDS = 60;
